import type { ReactNode } from 'react';

export interface StatCardProps {
  label: string;
  value: ReactNode;
  unit?: string;
  /** Optional tint for the value (e.g. 'accent' for totals, 'warn' for failures). */
  tone?: 'text' | 'accent' | 'warn' | 'good' | 'power';
  /** 'compact' trims the padding and the label gap for grids that must
   *  share a screen with a hero (the live view's 2×2 under the
   *  animation). Default is the card as it renders everywhere else. */
  density?: 'default' | 'compact';
  align?: 'left' | 'center';
}

/**
 * Bordered white stat card. Label sits above in small-caps mute,
 * value sits below in tabular-num bold. Used by ChargingPage,
 * SessionCompletePage, HistoryPage detail sheet.
 */
export function StatCard({ label, value, unit, tone = 'text', align = 'left', density = 'default' }: StatCardProps) {
  const color =
    tone === 'accent'
      ? 'var(--evi-accent)'
      : tone === 'warn'
        ? 'var(--evi-warn)'
        : tone === 'good'
          ? 'var(--evi-good)'
          : tone === 'power'
            ? 'var(--evi-power)'
            : 'var(--evi-text)';

  return (
    <div
      style={{
        background: 'var(--evi-surface)',
        border: '1px solid var(--evi-border)',
        borderRadius: 14,
        padding: density === 'compact' ? '9px 12px' : '12px 14px',
        textAlign: align,
        display: 'flex',
        flexDirection: 'column',
        gap: density === 'compact' ? 3 : 6,
        minWidth: 0,
      }}
    >
      <div
        style={{
          fontSize: 10,
          letterSpacing: 1,
          color: 'var(--evi-text-mute)',
          textTransform: 'uppercase',
          fontWeight: 500,
        }}
      >
        {label}
      </div>
      <div
        style={{
          // Reduced from 20 → 18 to give long alphanumeric IDs
          // (e.g. charger identities like "CU89TVWKPVJ8") room
          // before they need to wrap at the column boundary.
          fontSize: 18,
          fontWeight: 700,
          color,
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: '-0.3px',
          // Bumped 1.1 → 1.2 so the second wrapped line doesn't
          // crowd the first when an ID does break.
          lineHeight: 1.2,
          // flex-wrap so the unit pill can flow under the value
          // when both don't fit on one line.
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'baseline',
          gap: 4,
          minWidth: 0,
          // overflow-wrap:anywhere is the right primitive for
          // breaking inside long unbroken alphanumeric IDs that
          // have no natural break points (no spaces or hyphens).
          // Don't use ellipsis — drivers need to see the full
          // identifier to verify they're at the right charger.
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
        }}
      >
        {value}
        {unit && (
          <span
            style={{
              fontSize: 11,
              fontWeight: 500,
              color: 'var(--evi-text-mute)',
              letterSpacing: 0.5,
            }}
          >
            {unit}
          </span>
        )}
      </div>
    </div>
  );
}
